import "whatwg-fetch";

import { sha224 } from "js-sha256";

import { log } from "@/utils/logger";
import { uuid } from "@/utils/transform";

import { ApiResponse } from "./Model";
import { isProblem, Problem } from "./Problem";

export class Api {
  private host: string;

  public constructor(host = "") {
    this.host = host;
    this.extractJSON = this.extractJSON.bind(this);
    this.validateStatus = this.validateStatus.bind(this);
  }

  public async get<T>(
    action: string,
    query?: any,
    headers?: { [header: string]: string }
  ): Promise<ApiResponse<T>> {
    let url: string = this.host + action;
    if (query) {
      url += this.objToQueryString(query);
    }
    log.debug("Calling ws to get data", url);
    return window
      .fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...headers,
        },
      })
      .then<ApiResponse<T>>(this.extractJSON)
      .then(this.validateStatus);
  }

  public async post<T>(
    action: string,
    payload: any,
    headers?: { [header: string]: string }
  ): Promise<ApiResponse<T>> {
    const url: string = this.host + action;
    log.debug("Calling ws to create data", url);
    return window
      .fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify(payload),
      })
      .then<ApiResponse<T>>(this.extractJSON)
      .then(this.validateStatus);
  }

  public async put<T>(
    action: string,
    payload: any,
    headers?: { [header: string]: string }
  ): Promise<ApiResponse<T>> {
    const url: string = this.host + action;
    log.debug("Calling ws to update data", url);
    return window
      .fetch(url, {
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify(payload),
      })
      .then<ApiResponse<T>>(this.extractJSON)
      .then(this.validateStatus);
  }

  public async remove(
    action: string,
    headers?: { [header: string]: string }
  ): Promise<ApiResponse<void>> {
    const url: string = this.host + action;
    const fetchOptions = {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...headers,
      },
    };

    log.debug("Calling ws to delete data", url);

    return window
      .fetch(url, fetchOptions)
      .then<ApiResponse<void>>(this.extractJSON)
      .then(this.validateStatus);
  }

  public signRequest(): string {
    const token = uuid() + "." + new Date().getTime();
    return "INTERACTIVE " + token + "." + sha224(token);
  }

  private async extractJSON<T>(response: Response): Promise<ApiResponse<T>> {
    return response.text().then((text: string) => {
      const json: any = this.jsonTryParse(text);
      const ok: boolean = response.status >= 200 && response.status < 300;
      const data: any = ok ? json : undefined;
      const problem: Problem | undefined =
        !ok && json && isProblem(json) ? json : undefined;
      return {
        data,
        headers: response.headers,
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        ok,
        problem,
        text,
      };
    });
  }

  private validateStatus<T>(response: ApiResponse<T>): ApiResponse<T> {
    if (!response.ok) {
      log.error(
        `Invalid API response on page ${document.location} : ${JSON.stringify(
          response
        )} ua-full ${navigator.userAgent}`
      );
    }
    return response;
  }

  private objToQueryString(obj: { [key: string]: any }): string {
    return Object.keys(obj).reduce(function (
      str: string,
      key: string,
      i: number
    ): string {
      let delimiter, val;
      delimiter = i === 0 ? "?" : "&";
      key = encodeURIComponent(key);
      val = encodeURIComponent(obj[key]);
      return [str, delimiter, key, "=", val].join("");
    }, "");
  }

  private jsonTryParse(text: string): any {
    try {
      return JSON.parse(text);
    } catch (e) {
      return undefined;
    }
  }
}
